import { useMap } from '@vis.gl/react-google-maps';
import { useEffect, useRef } from 'react';

const NightOverlay = ({ enabled = true }: { enabled?: boolean }) => {
  const map = useMap();
  const polygonsRef = useRef<google.maps.Polygon[]>([]);

  useEffect(() => {
    if (!map) return;

    const update = () => {
      // Clear old polygons
      polygonsRef.current.forEach((p) => p.setMap(null));
      polygonsRef.current = [];

      if (!enabled) return;

      const strips = computeNightStrips(new Date());

      for (const strip of strips) {
        const poly = new google.maps.Polygon({
          paths: [strip],
          strokeWeight: 0,
          fillColor: '#000000',
          fillOpacity: 0.25,
          clickable: false,
          map,
        });
        polygonsRef.current.push(poly);
      }
    };

    update();
    const interval = setInterval(update, 60_000);

    return () => {
      clearInterval(interval);
      polygonsRef.current.forEach((p) => p.setMap(null));
    };
  }, [map, enabled]);

  return null;
};

export default NightOverlay;

const toRad = (d: number) => (d * Math.PI) / 180;
const toDeg = (r: number) => (r * 180) / Math.PI;

// Mercator cuts off near the poles, so clamp polygon vertices to the
// projection's usable range instead of using ±90.
const MAX_LAT = 85.05;

function computeNightStrips(date: Date): google.maps.LatLngLiteral[][] {
  const dayOfYear = getDayOfYear(date);
  const hours = date.getUTCHours() + date.getUTCMinutes() / 60 + date.getUTCSeconds() / 3600;

  const declination = toRad(-23.44 * Math.cos((2 * Math.PI / 365) * (dayOfYear + 10)));
  const sinDec = Math.sin(declination);
  const cosDec = Math.cos(declination);
  const subsolarLng = -(hours - 12) * 15;

  // Night pole: opposite hemisphere from the sun. Within a few hours of an
  // equinox the declination is ~0 and the terminator is a vertical line, so
  // nudge sinDec off zero to keep the terminator formula finite. The sign of
  // the nudge has to match the night pole we pick.
  const nightPole = declination >= 0 ? -MAX_LAT : MAX_LAT;
  const EPS = 1e-6;
  const safeSinDec = Math.abs(sinDec) < EPS ? (declination >= 0 ? EPS : -EPS) : sinDec;

  // Latitude where the sun is exactly on the horizon for this longitude.
  // atan (not atan2) keeps the result inside (-90°, 90°) for either sign of
  // the declination; the night side always runs from here to the night pole.
  const terminatorLat = (lngDeg: number) => {
    const h = toRad(lngDeg - subsolarLng);
    const lat = toDeg(Math.atan(-(cosDec * Math.cos(h)) / safeSinDec));
    return Math.max(-MAX_LAT, Math.min(MAX_LAT, lat));
  };

  // Build the night overlay as multiple small vertical strips.
  // Each strip covers 5° of longitude, so it's always a small polygon
  // that Google Maps won't "flip" to the complement.
  const stripWidth = 5;
  const strips: google.maps.LatLngLiteral[][] = [];

  for (let lng = -180; lng < 180; lng += stripWidth) {
    const lngLeft = lng;
    const lngRight = lng + stripWidth;

    // Top edge: along the terminator from left to right
    const points: google.maps.LatLngLiteral[] = [];
    for (let l = lngLeft; l <= lngRight; l += 1) {
      points.push({ lat: terminatorLat(l), lng: l });
    }

    // Fully lit strip: the terminator sits on the night pole, nothing to draw.
    if (points.every((p) => Math.abs(p.lat - nightPole) < 0.01)) continue;

    // Build the strip polygon: terminator points + night pole corners
    const strip: google.maps.LatLngLiteral[] = [...points];
    strip.push({ lat: nightPole, lng: lngRight });
    strip.push({ lat: nightPole, lng: lngLeft });

    strips.push(strip);
  }

  return strips;
}

function getDayOfYear(date: Date): number {
  const start = new Date(date.getFullYear(), 0, 0);
  const diff = date.getTime() - start.getTime();
  return Math.floor(diff / (1000 * 60 * 60 * 24));
}
